import type React from "react";
import {
  createContext,
  Dispatch,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { TourProgressBar } from "../components/TourProgressBar";
import type { TourDetailAction, TourDetailState } from "../types";
import { initialState, tourDetailReducer } from "./state";
import { useWebSocketEvent } from "@/libs/api/hooks/useWebSocketEvent";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useFetchTourDetails } from "../hooks/useTourDetailPage";

interface TourContextType extends TourDetailState {
  setContext: Dispatch<TourDetailAction>;
}

const TourDetailContext = createContext<TourContextType | undefined>(undefined);

export function TourDetailProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setContext] = useReducer(tourDetailReducer, initialState);

  const fetchDetails = useFetchTourDetails(state.tour.id, setContext);

  const { toast } = useToast();
  const { t } = useTranslation();

  const values = useMemo(
    () => ({
      ...state,
      setContext,
    }),
    [state],
  );

  useWebSocketEvent("tourChange", ({ type, tour }) => {
    if (type !== "updated" || tour.id !== state.tour.id) {
      return;
    }

    fetchDetails();

    toast({
      title: t("toasts.reOptimizedTour.success"),
      description: t("toasts.reOptimizedTour.description"),
      variant: "info",
      duration: 4000,
    });
  });

  return (
    <TourDetailContext.Provider value={values}>
      <TourProgressBar />

      {children}
    </TourDetailContext.Provider>
  );
}

export function useTourDetailContext() {
  const context = useContext(TourDetailContext);

  if (context === undefined) {
    throw new Error(
      "useTourDetailContext must be used within a TourDetailProvider",
    );
  }

  return context;
}
